import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
  Plus,
  Edit,
  Eye,
  Trash2,
  FileText,
  Globe,
  Smartphone,
  Monitor,
  Copy,
  Settings,
  Loader,
  Search,
  Filter,
  Layout,
  Code,
  Palette
} from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import TemplateBrowser from './TemplateBrowser';

const PageBuilder = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('my-pages'); // my-pages, templates, editor
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadPages();
  }, []);

  const loadPages = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.PUBLIC}/member/pages`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPages(data.pages || []);
      } else {
        toast.error('Failed to load pages');
      }
    } catch (error) {
      console.error('Error loading pages:', error);
      toast.error('Error loading pages');
    } finally {
      setLoading(false);
    }
  };

  const deletePage = async (pageId) => {
    if (!confirm('Are you sure you want to delete this page?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.PUBLIC}/member/pages/${pageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast.success('Page deleted successfully');
        loadPages();
      } else {
        toast.error('Failed to delete page');
      }
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Error deleting page');
    }
  };

  const duplicatePage = async (page) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_ENDPOINTS.PUBLIC}/member/pages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: `${page.name} (Copy)`,
          title: `${page.title} (Copy)`,
          html_content: page.html_content,
          page_type: page.page_type,
          category: page.category
        })
      });

      if (response.ok) {
        toast.success('Page duplicated successfully');
        loadPages();
      } else {
        toast.error('Failed to duplicate page');
      }
    } catch (error) {
      console.error('Error duplicating page:', error);
      toast.error('Error duplicating page');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPageTypeIcon = (pageType) => {
    switch(pageType) {
      case 'link-in-bio': return <Smartphone className="w-4 h-4" />;
      case 'lead-capture': return <FileText className="w-4 h-4" />;
      case 'landing': return <Globe className="w-4 h-4" />;
      default: return <Layout className="w-4 h-4" />;
    }
  };

  const getPageTypeColor = (pageType) => {
    switch(pageType) {
      case 'link-in-bio': return 'bg-purple-100 text-purple-800';
      case 'lead-capture': return 'bg-green-100 text-green-800';
      case 'landing': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredPages = pages.filter(page =>
    page.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    page.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Page Builder</h1>
          <p className="text-gray-600 mt-2">
            Create and manage your custom pages and funnels
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('my-pages')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'my-pages'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            My Pages ({pages.length})
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'templates'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Layout className="w-4 h-4 inline mr-2" />
            Template Library
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'my-pages' && (
        <div className="space-y-6">
          {/* Search and Actions */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search pages..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            
            <div className="flex space-x-2">
              <Button onClick={() => setActiveTab('templates')}>
                <Plus className="w-4 h-4 mr-2" />
                Create from Template
              </Button>
              <Button variant="outline" onClick={() => toast.info('Blank page creator coming soon!')}>
                <Code className="w-4 h-4 mr-2" />
                Blank Page
              </Button>
            </div>
          </div>

          {/* Pages List */}
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader className="w-8 h-8 animate-spin text-blue-600" />
            </div>
          ) : filteredPages.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {pages.length === 0 ? 'No pages yet' : 'No matching pages'}
              </h3>
              <p className="text-gray-500 mb-6">
                {pages.length === 0 
                  ? 'Create your first page using our professional templates'
                  : 'Try adjusting your search criteria'
                }
              </p>
              {pages.length === 0 && (
                <Button onClick={() => setActiveTab('templates')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Browse Templates
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPages.map((page) => (
                <Card key={page.id} className="group hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          {getPageTypeIcon(page.page_type)}
                          <h3 className="font-medium text-gray-900 truncate">{page.name}</h3>
                        </div>
                        
                        {page.title !== page.name && (
                          <p className="text-sm text-gray-600 mb-2 truncate">{page.title}</p>
                        )}
                        
                        <div className="flex items-center space-x-2 mb-2">
                          <span className={`text-xs px-2 py-1 rounded-full ${getPageTypeColor(page.page_type)}`}>
                            {page.page_type.replace('-', ' ')}
                          </span>
                          
                          {page.is_published ? (
                            <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                              Published
                            </span>
                          ) : (
                            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                              Draft
                            </span>
                          )}
                          
                          {page.is_mobile_responsive && (
                            <Smartphone className="w-4 h-4 text-green-600" />
                          )}
                        </div>
                        
                        <p className="text-xs text-gray-500">
                          Updated {formatDate(page.updated_at)}
                        </p>
                        
                        {page.metadata?.created_from_template && (
                          <p className="text-xs text-blue-600 mt-1">
                            From: {page.template_name}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between space-x-2">
                      <div className="flex space-x-1">
                        <Button size="sm" variant="outline" onClick={() => toast.info('Page editor coming soon!')}>
                          <Edit className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => toast.info('Page preview coming soon!')}>
                          <Eye className="w-3 h-3" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => duplicatePage(page)}>
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                      
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => deletePage(page.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'templates' && (
        <TemplateBrowser />
      )}
    </div>
  );
};

export default PageBuilder;